<template>
  <div class="left">
    <StepperCard
      path="/cart"
      title="Choose Your Dumpster"
      :isFutureStep="false"
      :isDumpsterStepper="true"
    />
    <h2 class="header">
      <span class=" header__number">2</span>Service Info &amp; Placement
    </h2>
    <p class="disclaimer disclaimer__italic">* indicates a required field</p>
    <form class="zip-search" @submit.prevent="continueClicked">
      <fieldset class="fieldset">
        <legend class="legend">When do you need the dumpster?</legend>
        <label class="label">
          Delivery Date *
          <Datepicker placeholder="Select Date"
            auto-apply
            required
            format="MMMM d yyyy"
            v-model="deliveryDate"
            :disabledWeekDays="[6, 0]"
            :minDate="new Date(minDeliveryDate)"
            :disabledDates="datesUnavailable"
            inputClassName="input"
          />
        </label>
        <Accordion class="mt-1 accordion">
          <template v-slot:header>
            <img :src="info" class="accordion__info-icon" alt="Info icon">
            <p class="accordion__heading">Our deliveries generally take place between 6 a.m. - 6 p.m. <span class="accordion__learn-more">Learn More</span></p>
          </template>
          <template v-slot:content>
            <p class="accordion__content">
              Our deliveries generally take place between 6 a.m. - 6 p.m., Monday through Friday. Our routes are dynamic and optimized for fuel-efficiency, so we cannot provide an ETA. To ensure your dumpster arrives on time, please schedule delivery for at least a day ahead of your project's start date.
            </p>
            <p class="accordion__content">
              Don't see the delivery date you need? Call us at <a :href="`tel:${brandPhoneNumber}`">{{brandPhoneNumber}}</a> during business hours to check for additional availability in your area.
            </p>
          </template>
        </Accordion>
      </fieldset>

      <fieldset class="fieldset mt-2">
        <legend class="legend">Do you have a deadline or a major event we should be aware of?</legend>
        <ExpandableSelect
          v-model="whiteGloveReason"
          :options="whiteGloveReasons"
          layout="rows"
          title="Choose one of the following options:"
        />
      </fieldset>

      <fieldset class="fieldset mt-2">
        <legend class="legend">Are you a Homeowner or a Contractor? *</legend>
        <div class="projects projects--two-column">
          <label :class="['projects__label', { 'projects__label--isSelected': selectedCustomerType === customerTypeHomeowner.value }]">
            <img :src="customerTypeHomeowner.icon" />
            <p class="projects__text">{{customerTypeHomeowner.label}}</p>
            <input
              v-model="selectedCustomerType"
              :disabled="!customerTypeHomeowner.canBeSelected"
              :value="customerTypeHomeowner.value"
              class="projects__radio"
              name="customer-type"
              type="radio"
              required
            />
          </label>
          <label :class="['projects__label', { 'projects__label--isSelected': selectedCustomerType === customerTypeContractor.value }]">
            <img :src="customerTypeContractor.icon" />
            <p class="projects__text">{{customerTypeContractor.label}}</p>
            <input
              v-model="selectedCustomerType"
              :disabled="!customerTypeContractor.canBeSelected"
              :value="customerTypeContractor.value"
              class="projects__radio"
              name="customer-type"
              type="radio"
              required
            />
          </label>
        </div>
      </fieldset>

       <dismissable v-if="selectedCustomerType === 'business'" :icon="contractorIcon" class="pro-tips">
        <template v-slot:content>
            <p v-html="proTips"></p>
        </template>
      </dismissable>

      <fieldset class="fieldset mt-1">
        <legend class="legend">Contact Information</legend>
        <label class="label">
          First Name *
          <input
            v-model="fname"
            class="input"
            placeholder="Enter Contact First Name*"
            type="text"
            required
          />
        </label>
        <label class="label">
          Last Name *
          <input
            v-model="lname"
            class="input"
            onchange="this.setCustomValidity('')"
            oninvalid="this.setCustomValidity('Last name required. Digits not allowed.')"
            pattern="[^0-9]+"
            placeholder="Enter Contact Last Name*"
            type="text"
            required
          />
        </label>
        <label v-if="selectedCustomerType === 'business'" class="label">
          Company *
          <input
            v-model="company"
            class="input"
            placeholder="Enter Contact Company*"
            type="text"
            required
          />
        </label>
        <label class="label">
          Email *
          <input
            v-model="email"
            class="input"
            onchange="this.setCustomValidity('')"
            oninvalid="this.setCustomValidity('This is not a valid email address.')"
            pattern="[a-z0-9._%+\-]+@[a-z0-9.\-]+\.[a-z]{2,}"
            placeholder="Enter Contact Email *"
            type="email"
            required
          />
        </label>
        <label class="label">
          Phone Number *
          <small class="zip-search__helper">Provide the number of the best person to contact for questions regarding this order</small>
          <input
            ref="customerPhone"
            v-maska="phoneMask"
            v-model="phoneNumber"
            class="input"
            onchange="this.setCustomValidity('')"
            oninvalid="this.setCustomValidity('Please enter a valid phone number.')"
            placeholder="(999) 999-9999 *"
            type="tel"
            required
          />
        </label>
        <label class="label">
          Apartment/Suite Number (Optional)
          <input
            v-model="serviceApartmentNumber"
            class="input"
            type="text"
          />
        </label>
      </fieldset>
      <div class="ecom checkbox">
        <label class="ecom checkbox__label">
          <div v-if="!billingSameAsDelivery" class="ecom checkbox__fauxbox"></div>
          <img class="ecom checkbox__check-img" v-if="billingSameAsDelivery" :src="activeCheckbox">
          <input
            v-model="billingSameAsDelivery"
            class="ecom checkbox__input"
            type="checkbox"
          />
          <span class="ecom checkbox__text">
            My Billing Information is the Same as my Delivery Information.
          </span>
        </label>
      </div>
      <label class="label onpoint-drop-location__label mt-1">
        Where would you like us to place the dumpster? *
        <textarea
          v-model="placementInstructions"
          class="textbox"
          placeholder="e.g. Place dumpster on the left side of the driveway."
          title="Placement Instructions"
          required
        >
        </textarea>
      </label>
      <Accordion class="mb-2 accordion">
        <template v-slot:header>
          <img :src="info" class="accordion__info-icon" alt="Info icon">
          <p class="accordion__heading">Does your placement require a permit? <span class="accordion__learn-more">Learn More</span></p>
        </template>
        <template v-slot:content>
          <p class="accordion__content">
            Some municipalities require a permit for a dumpster to be placed on the street or blocking a public right-of-way, like a sidewalk. If your dumpster will be on public property, please check with your city government about permit requirements before your dumpster delivery.
          </p>
        </template>
      </Accordion>
      <dismissable>
        <template v-slot:content>
          <p>Use the OnPoint<sup>®</sup> Placement Tool to mark where you'd like the dumpster to be placed on-site! Check out <a href="#deiliveryTips">these tips and guidelines</a> to learn more about how to choose a drop-off location and prepare for your delivery. </p>
        </template>
      </dismissable>
      <button type="button" v-if="isMobile()" @click="isOnpointModalOpen = true" class="btn btn--primary mt-1 mb-1 onpoint-drop-location-toggle">
        <img :src="onpointIcon" />
        <span>Mark Your Drop Location</span>
      </button>
      <OnPointDumpsterPlacement
        v-if="serviceAddress && !isMobile()"
        @call-back="onPointConfirmedCallback"
        :address="serviceAddress"
        :dumpsterType="selectedDumpsterSize"
        ref="onpointMap"
      />
      <div class="checkbox">
        <label class="checkbox__label">
          <div v-if="!placementConfirmation" class="checkbox__fauxbox"></div>
          <img class="checkbox__check-img" v-if="placementConfirmation" :src="activeCheckbox">
          <input
            v-model="placementConfirmation"
            class="checkbox__input"
            type="checkbox"
            required
          />
          <span class="checkbox__text">
            I have placed the dumpster in a clear, accessible area and grant the driver full discretion to drop the container in the closest possible location. <strong>I&nbsp;acknowledge that if my selected location is not accessible, or if I have not obtained a necessary permit for street placement, it may result in a failed delivery trip fee of $195. *</strong>
          </span>
        </label>
      </div>
      <button
        type="submit"
        id="continueToPaymentBtn"
        class="btn btn--secondary btn--orange mb-3">
        <span>Continue</span>
        <img :src="iconArrowRight">
      </button>
    </form>
    <StepperCard
      path="/"
      :number=3
      title="Payment Information"
      :isFutureStep="true"
    />
    <!-- OnPoint Modal -->
    <modal class="onpoint-modal" :isOpen=isOnpointModalOpen @close-modal="closeOnpointModal" :showClose="true">
      <template v-slot:content>
        <OnPointDumpsterPlacement
          v-if="serviceAddress && isMobile"
          @call-back="onPointConfirmedCallback"
          :address="serviceAddress"
          :dumpsterType="selectedDumpsterSize"
          ref="onpointMap"
          />
      </template>
    </modal>
  </div>
</template>
<script>

import axios from 'axios'
import VueDatePicker from '@vuepic/vue-datepicker'
import config from '@/config'
import { mapGetters } from 'vuex'
import { timestamp, getFormattedPhoneNumber, isGoogleEventsReady, getClientID, isMobile } from '@/utilities.js'
import StepperCard from '@/components/StepperCard.vue'
import Modal from '@/components/Modal.vue'
import Dismissable from '@/components/Dismissable.vue'
import Accordion from '@/components/Accordion.vue'
import OnPointDumpsterPlacement from '@/components/OnPointDumpsterPlacement.vue'
import ExpandableSelect from '@/components/ExpandableSelect'

const { cdn, phoneNumber, proTips } = config
export default {
  components: {
    StepperCard,
    Dismissable,
    Datepicker: VueDatePicker,
    OnPointDumpsterPlacement,
    Modal,
    Accordion,
    ExpandableSelect
  },
  beforeRouteEnter (to, from, next) {
    if (to.name === 'Service' && !(['ChooseDumpster', 'Payment'].includes(from.name))) {
      next('/')
    } else {
      next()
    }
  },
  data () {
    return {
      pattern: '1?[(][0-9]{3}[)] [0-9]{3}-[0-9]{4}',
      openDatepicker: false,
      iconArrowRight: `${cdn}icons-arrow-right.svg`,
      onpointIcon: `${cdn}onpoint-icon.svg`,
      info: `${cdn}icons-info-pro-tip.svg`,
      contractorIcon: `${cdn}contractor-icon.svg`,
      activeCheckbox: `${cdn}icons-active-checkbox.svg`,
      brandPhoneNumber: phoneNumber,
      isMobile,
      isOnpointModalOpen: false,
      phoneMask: ['(###) ###-####', '# (###) ###-####'],
      proTips,
      whiteGloveReasons: [
        {
          label: 'I’m moving',
          value: 'Life Event - Housing Change'
        },
        {
          label: 'I’m cleaning out a loved one’s home',
          value: 'Life Event - Death'
        },
        {
          label: 'I need the dumpster for a scheduled contractor',
          value: 'Crew Scheduled'
        },
        {
          label: 'I scheduled additional equipment for this project',
          value: 'Equipment Scheduled'
        },
        {
          label: 'I have a tight delivery deadline',
          value: 'Deadline for Delivery'
        }
      ]
    }
  },
  mounted () {
    const phoneInput = this.$refs.customerPhone
    phoneInput.addEventListener('keyup', (e) => {
      const frstChar = parseInt(e.target.value.replace(/[^\d.-]+/g, '').charAt(0))
      const mask = frstChar === 1 ? '1 (###) ###-####' : '(###) ###-####'
      e.target.setAttribute('data-mask', mask)
    })
    if (isGoogleEventsReady()) {
      const time = timestamp()

      window.gtag('event', 'Ecommerce - PageView', {
        event_category: 'Ecommerce',
        event_label: `${time}~${window.location.href}`,
        send_to: process.env.VUE_APP_UA_ID
      })

      window.gtag('event', 'Ecommerce - PageView', {
        event_category: 'Ecommerce',
        timestamp: time,
        page: 'service',
        bd_client_id: getClientID(),
        send_to: process.env.VUE_APP_GA4_ID
      })
    }

    if (!this.serviceAddress) {
      this.$store.commit('customer/preventStartingOver', true)
    }
  },
  methods: {
    closeOnpointModal () {
      this.isOnpointModalOpen = false
    },
    toggleDatepicker () {
      this.openDatepicker = !this.openDatepicker
    },
    continueClicked () {
      if (isGoogleEventsReady()) {
        const time = timestamp()
        const buttonText = document.getElementById('continueToPaymentBtn').innerText
        window.gtag('event', 'Ecommerce - ContinueToPayment', {
          event_category: 'Ecommerce',
          event_label: `${time}~${window.location.href}~${buttonText}`,
          send_to: process.env.VUE_APP_UA_ID
        })

        window.gtag('event', 'Ecommerce - ContinueToPayment', {
          event_category: 'Ecommerce',
          button_text: buttonText,
          timestamp: time,
          bd_client_id: getClientID(),
          send_to: process.env.VUE_APP_GA4_ID
        })
      }

      this.$router.push('/cart/payment')
    },
    showChangeAddressDialog () {
      document.getElementsByClassName('summary__address-edit')[0].click()
    },
    onPointConfirmedCallback (data) {
      this.onPointDumpsterLocationStaticMapUri = data.imageurl
      this.jsonScript = data.jsonScript
      const mapDom = document.querySelector('[role="equipment-map"]')
      const bdOverlayText = mapDom.querySelector('.bd-overlay-text > div')
      const successMsg = mapDom.querySelector('.bd-overlay-text > div').innerHTML
      return axios.post(process.env.VUE_APP_ONPOINT_ENDPOINT, { imageUrl: this.onPointDumpsterLocationStaticMapUri, refData: 'E-commerce map placement', jsonScript: this.jsonScript })
        .then((r) => {
          if (r.status && r.status === 201 && r.data.data) {
            const { data } = r.data
            const { url, publicUrl, uuid } = data

            const response = {
              url,
              uuid,
              publicUrl
            }

            this.savedLocationResponse = response
            bdOverlayText.innerHTML = successMsg
            this.$refs.onpointMap.showOverlay()
          } else {
            const errorMsg = 'The location could not be saved at this time.'
            bdOverlayText.innerHTML = errorMsg
            this.$refs.onpointMap.showOverlay()
          }
          mapDom.querySelectorAll('.bd-overlay-continue, .bd-overlay-close').forEach(e => {
            e.addEventListener('click', () => {
              this.$refs.onpointMap.hideOverlay()
              this.isOnpointModalOpen = false
            })
          })
        })
        .catch(e => {
          mapDom.querySelector('.bd-overlay-text > div').innerHTML = '<div class="bd-overlay-error">There was an error saving your placement. Please try again.</div>'
          this.$refs.onpointMap.showOverlay()
        })
    }
  },
  computed: {
    ...mapGetters({ minDeliveryDate: 'cart/minDeliveryDate', datesUnavailable: 'cart/datesUnavailable' }),
    requiredFieldsFilled () {
      const requiredFieldsFilled = this.serviceAddress && this.placementConfirmation
      this.$store.commit('customer/requiredFieldsMet', requiredFieldsFilled)
      return requiredFieldsFilled
    },
    customerTypeHomeowner () {
      return this.$store.state.customer.customerTypeHomeowner
    },
    customerTypeContractor () {
      return this.$store.state.customer.customerTypeContractor
    },
    selectedCustomerType: {
      get () {
        return this.$store.state.customer.selectedCustomerType
      },
      set (value) {
        this.$store.commit('customer/selectedCustomerType', value)
      }
    },
    holidays: {
      get () {
        return this.$store.state.dumpster.holidays
      }
    },
    serviceAddress: {
      get () {
        // parsing the serviceAddress since it was showing as Object Object
        const addressObject = this.$store.state.customer.serviceAddress
        return addressObject.streetAddress && `${addressObject.streetAddress} ${addressObject.streetName}, ${addressObject.city}, ${addressObject.state} ${addressObject.postalCode}`
      },
      set (value) {
        return value
      }
    },
    serviceApartmentNumber: {
      get () {
        return this.$store.state.customer.serviceAddress.apartmentNumber
      },
      set (value) {
        this.$store.commit('customer/serviceApartmentNumber', value)
      }
    },
    preventStartingOver: {
      get () {
        return this.$store.state.customer.preventStartingOver
      },
      set (value) {
        this.$store.commit('customer/preventStartingOver', value)
      }
    },
    placementInstructions: {
      get () {
        return this.$store.state.customer.placementInstructions
      },
      set (value) {
        this.$store.commit('customer/placementInstructions', value)
      }
    },
    deliveryDate: {
      get () {
        return this.$store.state.customer.deliveryDate
      },
      set (value) {
        this.$store.commit('customer/deliveryDate', value)
      }
    },
    whiteGloveReason: {
      get () {
        return this.$store.state.customer.whiteGloveReason
      },
      set (value) {
        this.$store.commit('customer/whiteGloveReason', value)
      }
    },
    fname: {
      get () {
        return this.$store.state.customer.fname
      },
      set (value) {
        this.$store.commit('customer/fname', value)
      }
    },
    lname: {
      get () {
        return this.$store.state.customer.lname
      },
      set (value) {
        this.$store.commit('customer/lname', value)
      }
    },
    company: {
      get () {
        return this.$store.state.customer.company
      },
      set (value) {
        this.$store.commit('customer/company', value)
      }
    },
    email: {
      get () {
        return this.$store.state.customer.email
      },
      set (value) {
        this.$store.commit('customer/email', value)
      }
    },
    phoneNumber: {
      get () {
        return this.$store.state.customer.phoneNumber
      },
      set (value) {
        this.$store.commit('customer/phoneNumber', getFormattedPhoneNumber(value))
      }
    },
    selectedDumpsterSize () {
      if (this.$store.state.dumpster.selectedDumpster) {
        return this.$store.state.dumpster.selectedDumpster.size
      }
      return 10
    },
    onPointDumpsterLocationStaticMapUri: {
      get () {
        return this.$store.state.onPoint.onPointDumpsterLocationStaticMapUri
      },
      set (value) {
        this.$store.commit('onPoint/onPointDumpsterLocationStaticMapUri', value)
      }
    },
    jsonScript: {
      get () {
        return this.$store.state.onPoint.jsonScript
      },
      set (value) {
        this.$store.commit('onPoint/jsonScript', value)
      }
    },
    savedLocationResponse: {
      get () {
        return this.$store.state.onPoint.savedLocationResponse
      },
      set (value) {
        this.$store.commit('onPoint/savedLocationResponse', value)
      }
    },
    placementConfirmation: {
      get () {
        return this.$store.state.customer.placementConfirmation
      },
      set (value) {
        this.$store.commit('customer/placementConfirmation', value)
      }
    },
    billingSameAsDelivery: {
      get () {
        return this.$store.state.customer.billingSameAsDelivery
      },
      set (value) {
        this.$store.commit('customer/billingSameAsDelivery', value)
      }
    }
  }
}
</script>
